import { DynamicMechanicalSample } from "@/pages/laboratory/models/DynamycMechanicalSample";
import { useTablesStyles } from "@/pages/shift-report/styles/useTablesStyles";
import { Grid2 as Grid, Paper } from "@mui/material";

import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import { Heat } from "../../../chemistry-heat-report/models/Heat";
import { CatalogGradeMechanicalLimit } from "../../../models/CatalogGradeMechanicalLimit";
import { CatalogMechanicalTest } from "../../../models/CatalogMechanicalTest";
import { CatalogResultSource } from "../../../models/CatalogResultSource";
import { MechanicalSample } from "../../../models/MechanicalSample";
import { useTableData } from "../hooks/useTableData";

type Props = {
	loadingGrid: boolean;
	heat: Heat | null;
	mechanicalSamples: MechanicalSample[];
	gradeLimits: CatalogGradeMechanicalLimit[];
	resultSources: CatalogResultSource[];
	mechanicalTests: CatalogMechanicalTest[];
};
export const TableMechanical = ({
	loadingGrid,
	heat,
	mechanicalSamples,
	gradeLimits,
	resultSources,
	mechanicalTests,
}: Props) => {
	const classes = useTablesStyles();
	const gridRef = useRef<AgGridReact<DynamicMechanicalSample>>(null);
	const { columns, defaultColDef } = useTableData({
		resultSources,
		mechanicalTests,
	});

	// Process rows data
	const rows = useMemo(() => {
		return mechanicalSamples.map((sample) => {
			const myObject: DynamicMechanicalSample = {
				...sample,
				elementParams: [],
				sampleParams: [
					"SampleSequence",
					"DateTimeResults",
					"StatusCode",
					"ResultSourceId",
					"Comments",
					"Technician",
					"Method",
					"HeatId",
					"Width",
					"Thickness",
					"Diameter",
				],
				testParams: [],
			};
			const samplesId = (sample.SamplesId ?? "").split(",");
			const samplesValue = (sample.SamplesValue ?? "").split(",");
			const samplesResult = (sample.ResultsId ?? "").split(",");

			// Set limits
			myObject.Limits = gradeLimits.filter(
				(limit) => limit.GradeId === sample.GradeId,
			);

			// Initialize mechanical tests with null values
			mechanicalTests.forEach((test) => {
				myObject[String(test.MechanicatTestId).trim()] = null;
			});

			// Set sample values
			samplesId.forEach((id, index) => {
				const value = samplesValue[index];
				myObject[id.trim()] = !isNaN(parseFloat(value))
					? parseFloat(value)
					: null;
			});

			return myObject;
		});
	}, [mechanicalSamples, gradeLimits, mechanicalTests]);

	// Grid style
	const gridStyle = useMemo(
		() => ({
			height: "auto",
		}),
		[],
	);

	// Handle loading overlay
	useMemo(() => {
		if (loadingGrid && gridRef.current?.api) {
			gridRef.current.api.showLoadingOverlay();
		} else if (gridRef.current?.api) {
			gridRef.current.api.hideOverlay();
		}
	}, [loadingGrid]);

	return (
		<Grid
			size={{ xs: 12 }}
			container
			className={classes.root}
			style={gridStyle}
		>
			<Grid size={{ xs: 12 }}>
				<Paper elevation={1}>
					<AgGridReact<DynamicMechanicalSample>
						ref={gridRef}
						rowSelection="single"
						pagination={false}
						animateRows={true}
						domLayout="autoHeight"
						tooltipShowDelay={0}
						tooltipHideDelay={2000}
						rowData={rows}
						columnDefs={columns}
						suppressContextMenu={true}
					/>
				</Paper>
			</Grid>
		</Grid>
	);
};
